import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  applyTheme,
  DEFAULT_PREFERENCES,
  type ThemeMode,
  type AccentName,
  type Density,
  type UiPreferences,
} from '@/shared/lib/theme';

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  // ── B-16: personalizacja UI (SOPZ G4) ──────────────────────────────────────
  theme: ThemeMode;
  accent: AccentName;
  highContrast: boolean;
  density: Density;
  /** Rozwiązany tryb ciemny — dla ikony w Topbarze / kodu, który pytał o darkMode. */
  darkMode: boolean;

  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  toggleCollapsed: () => void;

  toggleDarkMode: () => void;
  setDarkMode: (dark: boolean) => void;
  setTheme: (t: ThemeMode) => void;
  setAccent: (a: AccentName) => void;
  setHighContrast: (v: boolean) => void;
  setDensity: (d: Density) => void;
  /** Załaduj preferencje z konta (User.uiPreferences) i zastosuj + zbuforuj lokalnie. */
  hydratePreferences: (prefs: Partial<UiPreferences> | null | undefined) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => {
      const apply = (over?: Partial<Pick<UIState, 'theme' | 'accent' | 'highContrast' | 'density'>>) => {
        const s = { ...get(), ...over };
        const dark = applyTheme(
          { theme: s.theme, accent: s.accent, highContrast: s.highContrast, density: s.density },
          (d) => set({ darkMode: d }),
        );
        set({ darkMode: dark });
        return dark;
      };

      return {
        sidebarOpen: true,
        sidebarCollapsed: false,
        theme: DEFAULT_PREFERENCES.theme,
        accent: DEFAULT_PREFERENCES.accent,
        highContrast: DEFAULT_PREFERENCES.highContrast,
        density: DEFAULT_PREFERENCES.density,
        darkMode: false,

        setSidebarOpen: (open) => set({ sidebarOpen: open }),
        toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
        toggleCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

        // Szybki przełącznik w Topbarze — flip jasny/ciemny (trwały zapis do DB = Faza 3).
        toggleDarkMode: () => {
          const next: ThemeMode = get().darkMode ? 'light' : 'dark';
          set({ theme: next });
          apply({ theme: next });
        },
        setDarkMode: (dark) => {
          const next: ThemeMode = dark ? 'dark' : 'light';
          set({ theme: next });
          apply({ theme: next });
        },
        setTheme: (theme) => {
          set({ theme });
          apply({ theme });
        },
        setAccent: (accent) => {
          set({ accent });
          apply({ accent });
        },
        setHighContrast: (highContrast) => {
          set({ highContrast });
          apply({ highContrast });
        },
        setDensity: (density) => {
          set({ density });
          apply({ density });
        },

        hydratePreferences: (prefs) => {
          const merged = {
            theme: prefs?.theme ?? DEFAULT_PREFERENCES.theme,
            accent: prefs?.accent ?? DEFAULT_PREFERENCES.accent,
            highContrast: prefs?.highContrast ?? DEFAULT_PREFERENCES.highContrast,
            density: prefs?.density ?? DEFAULT_PREFERENCES.density,
          };
          set(merged);
          apply(merged);
        },
      };
    },
    {
      name: 'zur-ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        theme: s.theme,
        accent: s.accent,
        highContrast: s.highContrast,
        density: s.density,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Zastosuj zbuforowany motyw natychmiast (mitygacja FOUC), zanim wróci profil z DB.
        const dark = applyTheme(
          { theme: state.theme, accent: state.accent, highContrast: state.highContrast, density: state.density },
          (d) => useUIStore.setState({ darkMode: d }),
        );
        useUIStore.setState({ darkMode: dark });
      },
    },
  ),
);
